import { clearCoveoTokenAndOrg } from "@DSG/Coveo/Contex/Engine";
import ChevronSmall from "@DSG/Icons/ChevronSmall";
import User from "@DSG/MobiusOverrides/Icons/User";
import colorPallete, { buttonFonts, elevation, labelFonts } from "@DSG/MobiusOverrides/styles/variables";
import { getCookie,removeCookie,setCookie } from "@insite/client-framework/Common/Cookies";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import logger from "@insite/client-framework/Logger";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import setSignInOverlayIsOpen from "@insite/client-framework/Store/Components/SignInOverlay/Handlers/SetSignInOverlayIsOpen";
import {
    getCurrentUserIsGuest,
    getIsPunchOutSession,
    getSession,
    getSettingsCollection,
} from "@insite/client-framework/Store/Context/ContextSelectors";
import cancelPunchOut from "@insite/client-framework/Store/Context/Handlers/CancelPunchOut";
import setShowInventory from "@insite/client-framework/Store/Context/Handlers/SetShowInventory";
import setShowPricing from "@insite/client-framework/Store/Context/Handlers/SetShowPricing";
import signOut from "@insite/client-framework/Store/Context/Handlers/SignOut";
import { getCurrentPage, getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { useMergeStyles } from "@insite/content-library/additionalStyles";
import { NavigationModeCookieName } from "@insite/content-library/Components/NavigationModeMenu";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Hidden from "@insite/mobius/Hidden";
import Icon, { IconPresentationProps } from "@insite/mobius/Icon/Icon";
import ChevronDown from "@insite/mobius/Icons/ChevronDown";
import Eye from "@insite/mobius/Icons/Eye";
import EyeOff from "@insite/mobius/Icons/EyeOff";
import Menu, { MappedLink } from "@insite/mobius/Menu";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography/Typography";
import { breakpointMediaQueries } from "@insite/mobius/utilities";
import { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css, ThemeProps, withTheme } from "styled-components";

const enum fields {
    visibilityState = "visibilityState",
    icon = "icon",
    includeAccountMenu = "includeAccountMenu",
    showPricingToggle = "showPricingToggle",
    showInventoryToggle = "showInventoryToggle",
    myAccountSequence = "myAccountSequence",
}

const mapStateToProps = (state: ApplicationState) => {
    const currentMode = getCookie(NavigationModeCookieName) || "Storefront";
    const orderSettings = getSettingsCollection(state).orderSettings;
    const session = getSession(state);
    const isAuthenticated = session?.isAuthenticated && !session?.isGuest;
    const isVmiAdmin = session?.userRoles?.toLowerCase().indexOf("vmi_admin") !== -1;
    const isVmiUser = session?.userRoles?.toLowerCase().indexOf("vmi_user") !== -1;
    return {
        isSignInPage: getCurrentPage(state).type === "SignInPage",
        userName: state.context.session.userName,
        myAccountPageLink: getPageLinkByPageType(state, "MyAccountPage"),
        signInUrl: getPageLinkByPageType(state, "SignInPage")?.url,
        currentUserIsGuest: getCurrentUserIsGuest(state),
        isPunchOutSession: getIsPunchOutSession(state),
        currentLocation: getLocation(state),
        displayChangeCustomerLink: state.context.session?.displayChangeCustomerLink || false,
        displayPricingAndInventory: state.context.session?.displayPricingAndInventory || false,
        showInventory: state.context.showInventory,
        showPricing: state.context.showPricing,
        firstName: state.context.session.firstName,
        myAccountSequence: getSettingsCollection(state).customSettings,
        displayVmiNavigation: currentMode === "Vmi" && orderSettings.vmiEnabled && isAuthenticated && (isVmiAdmin || isVmiUser),
        currentBillToCompanyName:state.context.session?.properties["billToCompanyName"],
        currentBillToCustomerNumber:state.context.session?.properties["billToCustomerNumber"],
    }
};

const mapDispatchToProps = {
    signOut,
    cancelPunchOut,
    setSignInOverlayIsOpen,
    setShowInventory,
    setShowPricing,
};

export interface HeaderSignInStyles {
    signOutWrapper?: InjectableCss;
    titleClickable?: ClickablePresentationProps;
    titleIcon?: IconPresentationProps;
    titleTypography?: TypographyPresentationProps;
    createAccountTitle?: TypographyPresentationProps;
    signOutClickable?: ClickablePresentationProps;
}

export const headerSignInStyles: HeaderSignInStyles = {
    signOutWrapper: {
        css: css`
            [data-test-selector="header_signIn"] {
                display: flex;
                align-items: center;
                
                [data-test-selector="header_userName"] {
                    color: ${colorPallete.grey800} !important;
                }
            }

            [class*="IconWrapper-"]:not(.create-account-icon) {
                margin-right: 12px;
                @media only screen and (max-width: 768px) {
                    margin-right: 0;
                }
            }
        `,
    },
    titleClickable: {
        css: css`
            display: block;
            &.guestUser {
                .textWrapper {
                    display: flex;
                    flex-direction: column;
                }
                ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    null,
                    null,
                    css`
                        display: flex;
                        .textWrapper {
                            overflow: hidden;
                            position: absolute;
                            width: 0;
                            height: 0;
                            clip: rect(0, 0, 0, 0);
                        }
                    `,
                ], "max")}
            }
        `,
    },
    titleIcon: {
        size: 24,
    },
    titleTypography: {
        size: labelFonts.label1Regular.fontSize,
        lineHeight: labelFonts.label1Regular.lineHeight,
        css: css`
            display: block;
        `,
    },
    createAccountTitle: {
        size: labelFonts.label1SemiBold.fontSize,
        lineHeight: labelFonts.label1SemiBold.lineHeight,
        weight: labelFonts.label1SemiBold.fontWeight,
        css: css`
            display: flex;
            align-items: center;

            span {
                margin-left: 4px;
            }

            &:hover {
                color: ${colorPallete.primaryRed500};

                [class*="IconWrapper"] {
                    svg {
                        color: ${colorPallete.primaryRed500};
                    }
                }
            }
        `,
    },
    signOutClickable: {
        css: css`
            flex: 0 0 auto;
            margin-left: 16px;
            text-transform: uppercase;
            font-size: ${labelFonts.label1Regular.fontSize};
            line-height: ${labelFonts.label1Regular.lineHeight};
        `,
    },
};

interface OwnProps extends WidgetProps {
    fields: {
        [fields.visibilityState]: string;
        [fields.icon]: string;
        [fields.includeAccountMenu]: boolean;
        [fields.showPricingToggle]: boolean;
        [fields.showInventoryToggle]: boolean;
    };
}

type Props = OwnProps &
    ReturnType<typeof mapStateToProps> &
    ResolveThunks<typeof mapDispatchToProps> &
    HasHistory &
    ThemeProps<BaseTheme>;

let icon: React.ComponentType<IconPresentationProps>;

export const HeaderSignIn = ({
    userName,
    isSignInPage,
    currentUserIsGuest,
    signOut,
    cancelPunchOut,
    fields,
    history,
    myAccountPageLink,
    signInUrl,
    isPunchOutSession,
    currentLocation,
    displayChangeCustomerLink,
    displayPricingAndInventory,
    showInventory,
    showPricing,
    setSignInOverlayIsOpen,
    setShowInventory,
    setShowPricing,
    theme,
    firstName,
    myAccountSequence,
    displayVmiNavigation,
    currentBillToCompanyName,
    currentBillToCustomerNumber,
}: Props) => {
    const styles = useMergeStyles("headerSignIn", headerSignInStyles);

    useEffect(() => {
        window.onpageshow = function (event) {
            if (event.persisted) {
                window.location.reload();
            }
        };
    }, [userName]);

    if (!myAccountPageLink) {
        return null;
    }

    const { visibilityState, includeAccountMenu, showInventoryToggle, showPricingToggle } = fields;
    const showIcon = visibilityState === "both" || visibilityState === "icon";
    const showLabel = visibilityState === "both" || visibilityState === "label";

    if (fields.icon === "User") {
        icon = User;
    }

    const currentMode = getCookie(NavigationModeCookieName);
    let menuItems = (myAccountPageLink?.children?.filter(
        pageLink => !(pageLink.type === "ChangeCustomerPage" && !displayChangeCustomerLink),
    ) ?? []) as MappedLink[];

    const menuItemsFliter = [] as MappedLink[];

    const settings = myAccountSequence as any;
    const myArray = settings.myAccountLinkDesktop?.split(",");
    myArray?.forEach((item: string | undefined) => {
        const menuLink = myAccountPageLink?.children?.find(pageLink => pageLink.type === item);
        if (menuLink !== undefined) {
            menuItemsFliter.push(menuLink);
        }
    });

    menuItems = menuItemsFliter;
    const getMyAccountMenu = (title: "Account Overview"): MappedLink => {
        return {
            title: translate(title),
            url: myAccountPageLink.url,
            iconProps: {
                src: title,
                size: 22,
                css: css`
                    margin-left: 20px;
                    margin-right: -12px;
                `,
            },
        };
    };

    // menuItems.push(getMyAccountMenu("Account Overview"));
    menuItems.splice(0, 0, getMyAccountMenu("Account Overview"));
    const getMenuItem = (title: "Inventory" | "Pricing"): MappedLink => {
        return {
            title: title === "Inventory" ? translate("Inventory") : translate("Pricing"),
            iconProps: {
                src: (title === "Inventory" && showInventory) || (title === "Pricing" && showPricing) ? EyeOff : Eye,
                size: 22,
                css: css`
                    margin-left: 20px;
                    margin-right: -12px;
                `,
            },
            clickableProps: {
                css: css`
                    display: flex;
                    text-transform: uppercase;
                `,
                onClick: () => {
                    title === "Inventory"
                        ? setShowInventory({ showInventory: !showInventory })
                        : setShowPricing({ showPricing: !showPricing });
                },
            },
        };
    };

    const getViewAllMenu = (title: "View all"): MappedLink => {
        return {
            title: translate(title),
            url: myAccountPageLink.url,
            iconProps: {
                src: title,
                size: 22,
                css: css`
                    margin-left: 20px;
                    margin-right: -12px;
                `,
            },
        };
    };
    const getSignOutMenuItem = (title: "Sign out"): MappedLink => {
        return {
            title: translate(title),
            clickableProps: {
                css: css`
                    display: flex;
                    padding: 14px 32px;
                    max-width: 162px;
                    min-width: 162px;
                    justify-content: center;
                    border: 2px solid ${colorPallete.primaryRed500};
                    border-radius: 30px;
                    font-weight: bold;
                    margin: 16px auto;
                    span {
                        color: ${colorPallete.primaryRed500};
                        font-size: ${buttonFonts.buttonLg.fontSize};
                        line-height: ${buttonFonts.buttonLg.lineHeight};
                        font-weight: ${buttonFonts.buttonLg.fontWeight};
                    }

                    &:hover {
                        background: ${colorPallete.primaryMaroon500};
                        border: 2px solid ${colorPallete.primaryMaroon500};
                        span[class*="TypographyStyle-"] {
                            color: ${colorPallete.white};
                        }
                    }
                `,
                onClick: () => {
                    onSignOutHandler();
                },
            },
        };
    };

    if (!currentMode || currentMode === "Storefront") {
        menuItems = menuItems?.filter(pageLink => pageLink.type !== "VmiUsersPage");

        showInventoryToggle && displayPricingAndInventory && menuItems.push(getMenuItem("Inventory"));
        showPricingToggle && displayPricingAndInventory && menuItems.push(getMenuItem("Pricing"));
    }
    menuItems.push(getViewAllMenu("View all"));
    menuItems.push(getSignOutMenuItem("Sign out"));
    const onSignInHandler = (e: React.MouseEvent) => {
        if ((!userName || currentUserIsGuest) && !isSignInPage) {
            e.preventDefault();

            if (theme.signIn?.useOverlay) {
                setSignInOverlayIsOpen({ isOpen: true });
                return;
            }

            if (!signInUrl) {
                logger.warn("No url was found for SignInPage, defaulting to /SignIn");
                history.push("/MyAccount/SignIn");
            } else {
                const returnUrl = currentLocation.pathname + currentLocation.search;
                if (returnUrl === "/") {
                    history.push(`${signInUrl}`);
                } else {
                    history.push(`${signInUrl}?returnUrl=${encodeURIComponent(returnUrl)}&clientRedirect=true`);
                }
            }
        }
    };

    const onSignOutHandler = () => {
        isPunchOutSession ? cancelPunchOut() : signOut();
        clearCoveoTokenAndOrg();
    };

    removeCookie("CurrentBillToCompanyName");
    removeCookie("CurrentBillToCustomerNumber");
    removeCookie("UserName");
    let signInStatusText = translate("Sign In");
    removeCookie("isauthenticated", { path: "/" });
    if (userName && firstName && !currentUserIsGuest) {
        signInStatusText = `${translate("Hi, ")}${firstName}`;
        setCookie("CurrentBillToCompanyName", currentBillToCompanyName);
        setCookie("CurrentBillToCustomerNumber", currentBillToCustomerNumber);
        setCookie("CurrentUserName", userName);
        setCookie("isauthenticated", "true", { path: "/" });
    }

    let createAccountText = translate("Create Account");
    if (userName && firstName && !currentUserIsGuest) {
        createAccountText = translate("Account");
        setCookie("isauthenticated", "true", { path: "/" });
    }

   

    return (
        <>
            <StyledWrapper {...styles.signOutWrapper}>
                {displayVmiNavigation  && (
                    <Menu
                        className="hide-on-mobile hide-on-tablet accountMenu verticalMenuList"
                        descriptionId="accountMenu"
                        menuItems={[
                            {
                                title: translate("Sign Out"),
                                clickableProps: {
                                    onClick: () => {
                                        onSignOutHandler()
                                    }
                                }
                            },
                        ]}
                        width={194}
                        displayChangeCustomerLink={displayChangeCustomerLink}
                        maxDepth={1}
                        menuTrigger={
                        <Clickable role="button" {...styles.titleClickable} data-test-selector="header_signIn">
                            {showIcon && <Icon {...styles.titleIcon} src={icon} className="siginUserIcon" />}
                            {showLabel && (
                                <>
                                    <StyledWrapper>
                                        <Typography
                                            {...styles.titleTypography}
                                            data-test-selector="header_userName"
                                        >
                                            {signInStatusText}&nbsp;
                                        </Typography>
                                        <Typography
                                            {...styles.createAccountTitle}
                                            className="create-account-title"
                                        >
                                            {createAccountText} <Icon src={ChevronSmall} className="create-account-icon" />{" "}
                                        </Typography>
                                    </StyledWrapper>
                                </>
                            )}
                        </Clickable>
                    }
                    />
                )}
                {!displayVmiNavigation && (
                    <> 
                        {includeAccountMenu && userName && !currentUserIsGuest && (
                            <>
                                <Menu
                                    className="hide-on-mobile hide-on-tablet accountMenu verticalMenuList"
                                    descriptionId="accountMenu"
                                    menuItems={menuItems}
                                    width={194}
                                    displayChangeCustomerLink={displayChangeCustomerLink}
                                    maxDepth={1}
                                    menuTrigger={
                                        <Clickable role="button" {...styles.titleClickable} data-test-selector="header_signIn">
                                            {showIcon && <Icon {...styles.titleIcon} src={icon} className="siginUserIcon" />}
                                            {showLabel && (
                                                <>
                                                    <StyledWrapper>
                                                        <Typography
                                                            {...styles.titleTypography}
                                                            data-test-selector="header_userName"
                                                        >
                                                            {signInStatusText}&nbsp;
                                                        </Typography>
                                                        <Typography
                                                            {...styles.createAccountTitle}
                                                            className="create-account-title"
                                                        >
                                                            {createAccountText} <Icon src={ChevronSmall} className="create-account-icon" />{" "}
                                                        </Typography>
                                                    </StyledWrapper>
                                                </>
                                            )}
                                        </Clickable>
                                    }
                                    cssOverrides={{
                                        wrapper: css`
                                            min-width: 0;
                                        `,
                                        menu: css`
                                            right: -8px;
                                            box-shadow: ${elevation.dp08};
                                            padding: 0;
                                            border: 1px solid ${colorPallete.grey300};
                                            border-bottom-left-radius: 8px;
                                            border-bottom-right-radius: 8px;
                                        `,
                                        menuItem: css`
                                            a {
                                                min-height: usnet;
                                                padding: 8px 8px 8px 16px;

                                                &:hover {
                                                    background: ${colorPallete.grey300};

                                                    &::before {
                                                        content: none;
                                                    }
                                                }
                                            }
                                        `,
                                    }}
                                    data-test-selector="header_accountMenu"
                                />
                            </>
                        )}
                        {(!includeAccountMenu || !userName || (userName && currentUserIsGuest)) && (
                            <>
                                <Clickable
                                    {...styles.titleClickable}
                                    onClick={onSignInHandler}
                                    href={!signInUrl ? "/MyAccount/SignIn" : signInUrl}
                                    data-test-selector="header_signIn"
                                    className="guestUser"
                                >
                                    {showIcon && <Icon {...styles.titleIcon} src={icon} />}
                                    {showLabel && (
                                        <Typography className="textWrapper">
                                            <Typography {...styles.titleTypography}>{signInStatusText}&nbsp;</Typography>
                                            <Typography {...styles.createAccountTitle}>{createAccountText} </Typography>
                                        </Typography>
                                    )}
                                </Clickable>
                            </>
                        )}
                    </>
                )}
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(withHistory(withTheme(HeaderSignIn))),
    definition: {
        displayName: "Header Sign In",
        icon: "arrow-right-to-bracket",
        fieldDefinitions: [
            {
                name: fields.visibilityState,
                displayName: "Settings",
                editorTemplate: "RadioButtonsField",
                options: [
                    {
                        displayName: "Both",
                        value: "both",
                    },
                    {
                        displayName: "Show Label",
                        value: "label",
                    },
                    {
                        displayName: "Show Icon",
                        value: "icon",
                    },
                ],
                defaultValue: "both",
                fieldType: "General",
                sortOrder: 1,
            },
            {
                name: fields.icon,
                displayName: "SignIn Icon",
                editorTemplate: "DropDownField",
                options: [
                    {
                        displayName: "User",
                        value: "User",
                    },
                ],
                defaultValue: "User",
                fieldType: "General",
                sortOrder: 2,
            },
            {
                name: fields.includeAccountMenu,
                displayName: "Include My Account Menu",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.showPricingToggle,
                displayName: "Show/Hide Pricing Toggle",
                editorTemplate: "CheckboxField",
                isVisible: widget => widget.fields.includeAccountMenu,
                defaultValue: false,
                fieldType: "General",
                sortOrder: 4,
            },
            {
                name: fields.showInventoryToggle,
                displayName: "Show/Hide Inventory Toggle",
                editorTemplate: "CheckboxField",
                isVisible: widget => widget.fields.includeAccountMenu,
                defaultValue: false,
                fieldType: "General",
                sortOrder: 5,
            },
        ],
        group: "Common",
    },
};

export default widgetModule;
